// src/pages/StudentProfile/StudentProfile.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStudentProfile } from "../../hooks/useStudentProfile";
import { formatDate } from "../../utils";
import { getNamHocActive, getActiveHocKy } from "../../services/namHocService";

const StudentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const { student, lichSu, loading } = useStudentProfile(id, "CC1A");
  const [hocKyHienTai, setHocKyHienTai] = useState("HK1");
  const [namHocHienTai, setNamHocHienTai] = useState("2025-2026");

  useEffect(() => {
    const init = async () => {
      try {
        const nh = await getNamHocActive();
        if (nh) setNamHocHienTai(nh.tenNamHoc || nh.id || '2025-2026');
        const hk = await getActiveHocKy();
        if (hk) setHocKyHienTai(hk.hocKy);
      } catch(e) {}
    };
    init();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64 gap-2">
      <span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span>
      <span className="text-body-md text-on-surface-variant">Đang tải hồ sơ...</span>
    </div>
  );
  
  if (!student) return (
    <div className="text-center py-12 text-on-surface-variant">
      <span className="material-symbols-outlined text-[48px]">person_off</span>
      <p className="mt-2">Không tìm thấy học sinh</p>
      <button onClick={() => navigate(-1)} className="mt-3 px-4 py-2 bg-primary text-on-primary rounded-xl text-body-md font-semibold">Quay lại</button>
    </div>
  );

  const tenLop = student.lopHienTaiId || "Chưa có lớp";

  return (
    <div className="space-y-4 pb-20 md:pb-6">
      {/* Header */}
      <div className="bg-surface-container-lowest rounded-xl p-4 md:p-6 shadow-sm border border-outline-variant">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary-container text-on-primary-container flex items-center justify-center text-3xl font-bold flex-shrink-0">
            {student.hoTen?.charAt(0) || "?"}
          </div>
          <div>
            <h2 className="text-[22px] md:text-[26px] font-bold text-primary">
              {student.tenThanh ? `${student.tenThanh} - ` : ""}{student.hoTen}
            </h2>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-caption text-on-surface-variant">
              <span>🎂 {formatDate(student.ngaySinh)}</span>
              <span>· {student.gioiTinh}</span>
              <span>· 🏫 {tenLop}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-container rounded-xl p-1">
        <button onClick={() => setActiveTab('overview')} className={`flex-1 py-2.5 rounded-lg text-caption font-bold transition-colors ${activeTab === 'overview' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'}`}>📊 Tổng quan</button>
        <button onClick={() => setActiveTab('history')} className={`flex-1 py-2.5 rounded-lg text-caption font-bold transition-colors ${activeTab === 'history' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'}`}>📚 Học bạ</button>
      </div>

      {/* TAB TỔNG QUAN */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* Thông tin cá nhân */}
          <div className="bg-surface-container-lowest rounded-xl p-4 shadow-sm border border-outline-variant">
            <h3 className="text-body-lg font-bold mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">person</span>Thông tin cá nhân
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-body-md">
              <div className="flex justify-between md:flex-col"><span className="text-caption text-on-surface-variant">Họ tên</span><span className="font-semibold">{student.hoTen}</span></div>
              <div className="flex justify-between md:flex-col"><span className="text-caption text-on-surface-variant">Tên thánh</span><span className="font-semibold">{student.tenThanh || "—"}</span></div>
              <div className="flex justify-between md:flex-col">
                <span className="text-caption text-on-surface-variant">Phụ huynh (Bố)</span>
                <span className="font-semibold">
                  {student.tenThanhBo ? `${student.tenThanhBo} - ` : ""}{student.hoTenBo || "—"}
                  {student.sdtBo && <a href={`tel:${student.sdtBo}`} className="ml-2 text-primary">📞 {student.sdtBo}</a>}
                </span>
              </div>
              <div className="flex justify-between md:flex-col">
                <span className="text-caption text-on-surface-variant">Phụ huynh (Mẹ)</span>
                <span className="font-semibold">
                  {student.tenThanhMe ? `${student.tenThanhMe} - ` : ""}{student.hoTenMe || "—"}
                  {student.sdtMe && <a href={`tel:${student.sdtMe}`} className="ml-2 text-primary">📞 {student.sdtMe}</a>}
                </span>
              </div>
            </div>
          </div>

          {/* Hành trình đức tin */}
          <div className="bg-surface-container-lowest rounded-xl p-4 shadow-sm border border-outline-variant">
            <h3 className="text-body-lg font-bold mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">church</span>Hành trình đức tin
            </h3>
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1.5 rounded-full text-caption font-semibold ${student.ngayRuaToi ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                💧 Rửa Tội: {formatDate(student.ngayRuaToi) || "Chưa có"}
              </span>
              <span className={`px-3 py-1.5 rounded-full text-caption font-semibold ${student.ngayRuocLeLanDau ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                🍞 Rước Lễ: {formatDate(student.ngayRuocLeLanDau) || "Chưa có"}
              </span>
              <span className={`px-3 py-1.5 rounded-full text-caption font-semibold ${student.ngayThemSuc ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                🕊 Thêm Sức: {formatDate(student.ngayThemSuc) || "Chưa có"}
              </span>
            </div>
          </div>

          {/* Link Điểm danh */}
          <button onClick={() => navigate(`/diem-danh?search=${encodeURIComponent(student.hoTen)}`)}
            className="w-full bg-surface-container-lowest rounded-xl p-4 shadow-sm border border-outline-variant flex items-center justify-between hover:bg-surface-container transition-colors text-left">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-primary text-[28px]">fact_check</span>
              <div><p className="text-body-lg font-bold">Điểm danh</p><p className="text-caption text-on-surface-variant">{hocKyHienTai} · {namHocHienTai}</p></div>
            </div>
            <span className="material-symbols-outlined text-outline">arrow_forward</span>
          </button>

          {/* Link Điểm số */}
          <button onClick={() => navigate(`/nhap-diem?search=${encodeURIComponent(student.hoTen)}`)}
            className="w-full bg-surface-container-lowest rounded-xl p-4 shadow-sm border border-outline-variant flex items-center justify-between hover:bg-surface-container transition-colors text-left">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-primary text-[28px]">edit_note</span>
              <div><p className="text-body-lg font-bold">Điểm số</p><p className="text-caption text-on-surface-variant">{hocKyHienTai} · {namHocHienTai}</p></div>
            </div>
            <span className="material-symbols-outlined text-outline">arrow_forward</span>
          </button>

          {/* Link Đánh giá */}
          <button onClick={() => navigate(`/danh-gia-cuoi-ky?search=${encodeURIComponent(student.hoTen)}`)}
            className="w-full bg-surface-container-lowest rounded-xl p-4 shadow-sm border border-outline-variant flex items-center justify-between hover:bg-surface-container transition-colors text-left">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-primary text-[28px]">grading</span>
              <div><p className="text-body-lg font-bold">Đánh giá</p><p className="text-caption text-on-surface-variant">{hocKyHienTai} · {namHocHienTai}</p></div>
            </div>
            <span className="material-symbols-outlined text-outline">arrow_forward</span>
          </button>
        </div>
      )}

      {/* TAB HỌC BẠ */}
      {activeTab === 'history' && (
        <div className="space-y-3">
          {lichSu.length > 0 ? (
            lichSu.map((item, idx) => (
              <div key={idx} className="bg-surface-container-lowest rounded-xl p-4 shadow-sm border border-outline-variant">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body-md font-bold">📚 {item.namHoc}</span>
                  <span className={`px-2 py-0.5 rounded-full text-label-caps font-bold ${item.trangThai === 'dangHoc' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{item.trangThai === 'dangHoc' ? '🟢 Đang học' : `🔵 ${item.trangThai}`}</span>
                </div>
                <p className="text-body-md">Lớp: <strong>{item.tenLop}</strong></p>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-on-surface-variant">
              <span className="material-symbols-outlined text-[48px]">inbox</span>
              <p className="mt-2">Chưa có dữ liệu học bạ</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentProfile;